import { useEffect, useState, type ReactNode } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { supabase } from "@/lib/supabase";
import type { Session } from "@supabase/supabase-js";

const PUBLIC_ROUTES = [
  "/auth/login",
  "/auth/signup",
  "/auth/forgot-password",
  "/auth/reset-password",
  "/zaproszenie",
];
const ONBOARDING_ROUTE = "/onboarding";

type AuthGuardProps = {
  children: ReactNode;
};

export function AuthGuard({ children }: AuthGuardProps) {
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);
  const [onboardingChecked, setOnboardingChecked] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    supabase.auth.getSession().then(({ data: { session: s } }) => {
      setSession(s);
      setLoading(false);
    });

    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((event, s) => {
      setSession(s);

      // Reset onboarding check when user changes or signs out.
      // Event-based (not user-id comparison) to avoid stale closure.
      if (event === "SIGNED_OUT" || event === "SIGNED_IN") {
        setOnboardingChecked(false);
      }
    });

    return () => subscription.unsubscribe();
  }, []);

  useEffect(() => {
    if (loading) return;

    const isPublicRoute = PUBLIC_ROUTES.some((r) => location.pathname.startsWith(r));
    const isOnboarding = location.pathname.startsWith(ONBOARDING_ROUTE);

    if (!session && !isPublicRoute) {
      navigate("/auth/login", { replace: true });
      return;
    }

    // Redirect logged-in users away from public routes — EXCEPT reset-password
    // (recovery tokens auto-create a session via detectSessionInUrl, but user
    // still needs to stay on the page to set a new password)
    if (
      session &&
      isPublicRoute &&
      !location.pathname.startsWith("/auth/reset-password") &&
      !location.pathname.startsWith("/zaproszenie")
    ) {
      navigate("/", { replace: true });
      return;
    }

    if (session && !isPublicRoute && !isOnboarding && !onboardingChecked) {
      supabase
        .from("organization_members")
        .select("organization_id, organizations(onboarding_completed_at)")
        .eq("user_id", session.user.id)
        .order("joined_at", { ascending: true })
        .limit(1)
        .maybeSingle()
        .then(({ data, error }) => {
          setOnboardingChecked(true);

          // Error other than "no rows" — log but don't crash
          if (error) {
            console.error("AuthGuard membership check failed:", error);
            return;
          }

          // No memberships (race condition after signup, or orphan user)
          if (!data) {
            navigate("/onboarding", { replace: true });
            return;
          }

          const org = data.organizations as unknown as {
            onboarding_completed_at: string | null;
          } | null;
          if (org && !org.onboarding_completed_at) {
            navigate("/onboarding", { replace: true });
          }
        });
    }
  }, [session, loading, location.pathname, navigate, onboardingChecked]);

  if (loading) {
    return (
      <div className="flex h-screen items-center justify-center">
        <div className="h-8 w-8 animate-pulse rounded-full bg-navy" />
      </div>
    );
  }

  return <>{children}</>;
}
